import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Block renderers for the four message-block kinds the runtime emits:
//   md     — markdown body (delegates to react-markdown so we get tables,
//            lists, links, code fences, etc. for free)
//   think  — collapsible reasoning block (KTK-216 surface)
//   tool   — collapsible tool call with kind-specific output renderers
//   stream — partial assistant text mid-stream
//
// Tool output renderers cover shell (terminal pane), web search (result
// list), net (request table), and diff (unified diff). Anything else
// falls back to a JSON/text dump.
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CodeBlock } from "./CodeBlock.js";
import { Expandable } from "./Expandable.js";
export function MdBlock({ content }) {
    return (_jsx("div", { className: "md", children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: {
                code({ inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className ?? "");
                    const text = String(children ?? "").replace(/\n$/, "");
                    if (inline || !match) {
                        return (_jsx("code", { className: className, ...props, children: children }));
                    }
                    return _jsx(CodeBlock, { lang: match[1], children: text });
                },
            }, children: content }) }));
}
export function ThinkBlock({ content, duration, open, status, }) {
    return (_jsx(Expandable, { kind: "think", label: "thought", duration: duration, status: status, defaultOpen: open, children: _jsx("div", { className: "exp__think", children: content.split("\n\n").map((p, i) => (_jsx("p", { children: p }, i))) }) }));
}
const TOOL_LABEL = {
    search: "searched the web",
    web: "fetched url",
    file: "read file",
    edit: "edited file",
    terminal: "ran command",
    bash: "ran command",
    code: "executed code",
    devtools: "chrome devtools",
    browser: "navigated browser",
    network: "captured network",
    db: "queried database",
    tool: "called tool",
    think: "thought",
};
export function ToolCallBlock({ tool, toolKind, args, output, duration, open, status = "done", }) {
    const kind = toolKind ?? "tool";
    // Tool name as the primary label so the user can see *what* ran
    // without expanding the block (Akari 2026-04-28: "I want to be
    // able to see what tool was run without expanding the tool call
    // blocks"). Falls back to the kind-generic ("ran command" / "called
    // tool") when the tool name isn't set.
    const label = tool ?? TOOL_LABEL[kind] ?? "called tool";
    const sub = previewArgs(args);
    const argsObj = args && typeof args === "object" ? args : null;
    const isShell = output && typeof output === "object" && "kind" in output && output.kind === "shell";
    return (_jsxs(Expandable, { kind: kind, label: label, sub: sub, duration: duration, status: status, defaultOpen: open, children: [argsObj && !isShell && (_jsx("div", { className: "exp__kv", children: Object.entries(argsObj)
                    .filter(([k]) => k !== "cmd")
                    .map(([k, v]) => (_jsxs("div", { className: "exp__kv-row", children: [_jsx("span", { className: "exp__kv-k", children: k }), _jsx("span", { className: "exp__kv-v", children: typeof v === "string" ? v : JSON.stringify(v) })] }, k))) })), output != null && _jsx(ToolOutputRenderer, { output: output })] }));
}
function previewArgs(args) {
    if (typeof args === "string")
        return args;
    if (!args || typeof args !== "object")
        return undefined;
    const a = args;
    if (typeof a.cmd === "string")
        return a.cmd;
    if (typeof a.url === "string")
        return a.url;
    if (typeof a.path === "string")
        return a.path;
    if (typeof a.query === "string")
        return `"${a.query}"`;
    if (typeof a.expression === "string")
        return a.expression;
    return undefined;
}
function ToolOutputRenderer({ output }) {
    if (typeof output === "string")
        return _jsx("div", { className: "exp__tool-out", children: output });
    if (output == null)
        return null;
    const o = output;
    if (o.kind === "shell") {
        const sh = output;
        return (_jsxs("div", { className: "shell", children: [sh.lines.map((l, i) => (_jsxs("div", { className: `shell__l shell__l--${l.t ?? "out"}`, children: [l.t === "cmd" && _jsx("span", { className: "shell__p", children: "$" }), _jsx("span", { children: l.text })] }, i))), sh.exit != null && (_jsxs("div", { className: `shell__exit shell__exit--${sh.exit === 0 ? "ok" : "err"}`, children: ["exit ", sh.exit] }))] }));
    }
    if (o.kind === "web") {
        const w = output;
        return (_jsx("div", { className: "webres", children: w.results.map((r, i) => (_jsxs("div", { className: "webres__row", children: [_jsxs("div", { className: "webres__head", children: [_jsxs("span", { className: "webres__num", children: [i + 1, "."] }), _jsx("a", { className: "webres__title", href: r.url, rel: "noreferrer noopener", target: "_blank", children: r.title })] }), _jsx("div", { className: "webres__url", children: r.url }), _jsx("div", { className: "webres__snip", children: r.snippet })] }, i))) }));
    }
    if (o.kind === "net") {
        const n = output;
        return (_jsxs("div", { className: "net", children: [_jsxs("div", { className: "net__head", children: [_jsx("span", { children: "method" }), _jsx("span", { children: "url" }), _jsx("span", { children: "status" }), _jsx("span", { children: "ms" })] }), n.reqs.map((r, i) => (_jsxs("div", { className: "net__row", "data-status": typeof r.status === "number"
                        ? r.status >= 400
                            ? "err"
                            : r.status >= 300
                                ? "warn"
                                : "ok"
                        : "ok", children: [_jsx("span", { className: "net__m", children: r.method }), _jsx("span", { className: "net__u", children: r.url }), _jsx("span", { className: "net__s", children: r.status }), _jsx("span", { className: "net__t", children: r.ms })] }, i)))] }));
    }
    if (o.kind === "diff") {
        const d = output;
        return (_jsxs("div", { className: "diff", children: [_jsx("div", { className: "diff__head", children: d.path }), _jsx("pre", { children: d.lines.map((l, i) => (_jsx("div", { className: `diff__l diff__l--${l[0] === "+" ? "add" : l[0] === "-" ? "del" : "ctx"}`, children: l }, i))) })] }));
    }
    return _jsx("div", { className: "exp__tool-out", children: JSON.stringify(output, null, 2) });
}
export function StreamBlock({ content }) {
    return _jsx("span", { className: "streaming", children: content });
}
export function renderBlock(b, key) {
    switch (b.kind) {
        case "md":
            return _jsx(MdBlock, { content: b.content }, key);
        case "think":
            return _jsx(ThinkBlock, { content: b.content, duration: b.duration, open: b.open, status: b.status }, key);
        case "tool":
            return (_jsx(ToolCallBlock, { tool: b.tool, toolKind: b.toolKind, args: b.args, output: b.output, duration: b.duration, open: b.open, status: b.status }, key));
        case "stream":
            return _jsx(StreamBlock, { content: b.content }, key);
    }
}
//# sourceMappingURL=blocks.js.map